/**
 * System Fields (frontend mirror of backend/src/engine/systemFields.ts).
 *
 * Synthetic, always-available fields that are computed at dispatch time from
 * the lead's ingest instant (received_at) in the org's timezone — never stored
 * on the lead. Rendered as a "System Fields" group in every leg's field-mapping
 * picker. Keep this list in lockstep with the backend module.
 */

export interface SystemFieldOption {
  id: string;      // canonical namespaced key, e.g. 'system.import_date'
  label: string;   // shown in the picker
  example: string; // illustrative output (tooltips/help)
}

export const SYSTEM_FIELDS: SystemFieldOption[] = [
  { id: 'system.import_date',        label: 'Import Date (ISO)',                    example: '2026-07-29' },
  { id: 'system.import_date_us',     label: 'Import Date (US)',                     example: '07/29/2026' },
  { id: 'system.import_date_eu',     label: 'Import Date (EU)',                     example: '29/07/2026' },
  { id: 'system.import_datetime',    label: 'Import Date + Time (24h)',             example: '2026-07-29 14:35:02' },
  { id: 'system.import_datetime_us', label: 'Import Date + Time (US 12h)',          example: '07/29/2026 02:35:02 PM' },
  { id: 'system.import_iso8601',     label: 'Import Timestamp (ISO-8601 + offset)', example: '2026-07-29T14:35:02-07:00' },
  { id: 'system.import_unix',        label: 'Import Timestamp (Unix epoch)',        example: '1785101702' },
];

export const SYSTEM_FIELD_IDS = new Set(SYSTEM_FIELDS.map(f => f.id));

/** True if a mapping value is one of the synthetic system field ids. */
export function isSystemFieldId(value: string | null | undefined): boolean {
  return typeof value === 'string' && SYSTEM_FIELD_IDS.has(value);
}

/** Label for a system field id (falls back to the id itself). */
export function systemFieldLabel(id: string): string {
  return SYSTEM_FIELDS.find(f => f.id === id)?.label ?? id;
}
